import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Send, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function ContactSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent successfully! We will be in touch.");
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email");
      return;
    }
    setNewsletterLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Welcome to the T.I.I.N.A. community!");
      setNewsletterEmail("");
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("This email is already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-10 sm:py-14 overflow-hidden"
      style={{ backgroundColor: "#45443f" }}
    >

      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* Invitation header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-3xl"
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-white mb-4">
            An Invitation
          </p>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05] mb-6">
            Bring your <span className="text-white">fire.</span>
          </h2>
          <p className="text-white/90 text-base sm:text-lg leading-relaxed">
            This framework is alive. It will grow, adapt, and evolve, shaped by
            communities, partners, and our values. Whether you are a community
            member, innovator, partner, funder, or dreamer - bring your
            relationships, wisdom, courage, and fire.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-1">
          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3 bg-white/5 border border-white/10 p-8 sm:p-10"
          >
            <h3 className="font-['Syne'] text-2xl sm:text-3xl font-semibold text-white mb-2">
              Get in Touch
            </h3>
            <p className="text-white/40 text-sm mb-6">
              Or email us directly at{" "}
              <a href="mailto:Info@tiina.co.nz" className="text-white hover:text-white/80 transition-colors" data-testid="contact-email-link">
                Info@tiina.co.nz
              </a>
            </p>
            <form
              onSubmit={handleContactSubmit}
              data-testid="contact-form"
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-white/70 mb-2 block">
                    Name *
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    placeholder="Your name"
                    className="rounded-none bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-[#bb321a] focus-visible:ring-[#bb321a]/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-[0.15em] uppercase text-white/70 mb-2 block">
                    Email *
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    placeholder="your@email.com"
                    className="rounded-none bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-[#bb321a] focus-visible:ring-[#bb321a]/30"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold tracking-[0.15em] uppercase text-white/70 mb-2 block">
                  Subject
                </label>
                <Input
                  data-testid="contact-subject-input"
                  value={contactForm.subject}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, subject: e.target.value })
                  }
                  placeholder="What is this about?"
                  className="rounded-none bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-[#bb321a] focus-visible:ring-[#bb321a]/30"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-[0.15em] uppercase text-white/70 mb-2 block">
                  Message *
                </label>
                <Textarea
                  data-testid="contact-message-input"
                  value={contactForm.message}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, message: e.target.value })
                  }
                  placeholder="Tell us about your vision..."
                  rows={5}
                  className="rounded-none bg-transparent border-white/20 text-white placeholder:text-white/30 focus:border-[#bb321a] focus-visible:ring-[#bb321a]/30 resize-none"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={contactLoading}
                className="inline-flex items-center gap-2 px-8 py-4 text-white font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                style={{ background: "linear-gradient(135deg, #bb321a, #485e23, #364225)" }}
              >
                <Send className="w-4 h-4" />
                {contactLoading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </motion.div>

          {/* Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-2 flex flex-col"
          >
            <div className="bg-gradient-to-br from-[#bb321a] via-[#485e23] to-[#364225] p-8 sm:p-10 flex-1 relative overflow-hidden">
              <div className="relative z-10">
                <Mail className="w-10 h-10 text-white/80 mb-6" />
                <h3 className="font-['Syne'] text-2xl sm:text-3xl font-semibold text-white mb-3">
                  Stay Connected
                </h3>
                <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-8">
                  Join our community and receive updates on T.I.I.N.A.'s work,
                  events, and opportunities to get involved.
                </p>
                <form
                  onSubmit={handleNewsletterSubmit}
                  data-testid="newsletter-form"
                  className="space-y-4"
                >
                  <Input
                    data-testid="newsletter-email-input"
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="rounded-none bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:border-white focus-visible:ring-white/30"
                  />
                  <button
                    type="submit"
                    data-testid="newsletter-submit-button"
                    disabled={newsletterLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-white text-white font-bold text-sm uppercase tracking-wider hover:bg-white/90 transition-all duration-200 disabled:opacity-50"
                  >
                    <ArrowRight className="w-4 h-4" />
                    {newsletterLoading ? "Subscribing..." : "Subscribe"}
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
